﻿// Disabled as is externally copied in
/* eslint-disable */
import * as React from "react";
import css from "dom-css";
import { Utils } from "../../Utilities";

export type ScrollableProps = {
    className?: string;
    onScroll?: (e: Event) => void;
    onScrollFrame?: (values: { scrollTop: number; scrollLeft: number }) => void;
    onScrollStart?: () => void;
    onScrollStop?: () => void;
    onUpdate?: (values: { scrollTop: number; scrollLeft: number }) => void;
    style?: React.CSSProperties;
} & Partial<scrollableDefaultProps>;

type scrollableDefaultProps = Readonly<typeof ScrollableDefaultProps>;

export const ScrollableDefaultProps = {
    autoHeight: false,
    autoHeightMax: 200 as number | string,
    autoHeightMin: 0 as number | string,
    autoHide: true,
    autoHideDuration: 200,
    autoHideTimeout: 1000,
    hideTracksWhenNotNeeded: false,
    renderThumbHorizontal: renderThumbHorizontalDefault,
    renderThumbVertical: renderThumbVerticalDefault,
    renderTrackHorizontal: renderTrackHorizontalDefault,
    renderTrackVertical: renderTrackVerticalDefault,
    renderView: renderViewDefault,
    tagName: `div`,
    thumbMinSize: 30,
    thumbSize: 50,
    universal: false,
};

export interface IScrollableState {
    didMountUniversal: boolean;
}

export class Scrollable extends React.Component<
    ScrollableProps,
    IScrollableState
> {
    static defaultProps = ScrollableDefaultProps;

    constructor(props: ScrollableProps, ...rest) {
        super(props, ...rest);

        this._isScrolling = false;

        this.getScrollLeft = this.getScrollLeft.bind(this);
        this.getScrollTop = this.getScrollTop.bind(this);
        this.getScrollWidth = this.getScrollWidth.bind(this);
        this.getScrollHeight = this.getScrollHeight.bind(this);
        this.getClientWidth = this.getClientWidth.bind(this);
        this.getClientHeight = this.getClientHeight.bind(this);
        this.getValues = this.getValues.bind(this);
        this.getThumbHorizontalWidth = this.getThumbHorizontalWidth.bind(this);
        this.getThumbVerticalHeight = this.getThumbVerticalHeight.bind(this);
        this.getScrollLeftForOffset = this.getScrollLeftForOffset.bind(this);
        this.getScrollTopForOffset = this.getScrollTopForOffset.bind(this);

        this.scrollLeft = this.scrollLeft.bind(this);
        this.scrollTop = this.scrollTop.bind(this);
        this.scrollToLeft = this.scrollToLeft.bind(this);
        this.scrollToTop = this.scrollToTop.bind(this);
        this.scrollToRight = this.scrollToRight.bind(this);
        this.scrollToBottom = this.scrollToBottom.bind(this);

        this.handleTrackMouseEnter = this.handleTrackMouseEnter.bind(this);
        this.handleTrackMouseLeave = this.handleTrackMouseLeave.bind(this);
        this.handleHorizontalTrackMouseDown =
            this.handleHorizontalTrackMouseDown.bind(this);
        this.handleVerticalTrackMouseDown =
            this.handleVerticalTrackMouseDown.bind(this);
        this.handleHorizontalThumbMouseDown =
            this.handleHorizontalThumbMouseDown.bind(this);
        this.handleVerticalThumbMouseDown =
            this.handleVerticalThumbMouseDown.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleDrag = this.handleDrag.bind(this);
        this.handleDragEnd = this.handleDragEnd.bind(this);

        this.state = {
            didMountUniversal: false,
        };
    }

    private _requestFrame: number;

    private _hideTracksTimeout: number;

    private _detectScrollingInterval: number;

    private _isScrolling: boolean;

    private _isDragging: boolean;

    private _trackHorizontal: HTMLElement;

    private _trackVertical: HTMLElement;

    private _scrollbarWidth: number;

    private _thumbHorizontal: HTMLElement;

    private _thumbVertical: HTMLElement;

    private _view: HTMLElement;

    private _viewScrollLeft: number;

    private _lastViewScrollLeft: number;

    private _viewScrollTop: number;

    private _lastViewScrollTop: number;

    private _prevPageX: number;

    private _prevPageY: number;

    private _trackMouseOver: boolean;

    container: Element;

    componentDidMount(): void {
        this.addListeners();
        this.update();
        this.componentDidMountUniversal();
    }

    componentDidMountUniversal(): void {
        const { universal } = this.props;

        if (!universal) {
            return;
        }

        this.setState({
            didMountUniversal: true,
        });
    }

    componentDidUpdate(): void {
        this.update();
    }

    componentWillUnmount(): void {
        this.removeListeners();
        cancelAnimationFrame(this._requestFrame);
        clearTimeout(this._hideTracksTimeout);
        clearInterval(this._detectScrollingInterval);
    }

    getScrollLeft(): number {
        if (!this._view) {
            return 0;
        }
        return this._view.scrollLeft;
    }

    getScrollTop(): number {
        if (!this._view) {
            return 0;
        }
        return this._view.scrollTop;
    }

    getScrollWidth(): number {
        if (!this._view) {
            return 0;
        }
        return this._view.scrollWidth;
    }

    getScrollHeight(): number {
        if (!this._view) {
            return 0;
        }
        return this._view.scrollHeight;
    }

    getClientWidth(): number {
        if (!this._view) {
            return 0;
        }
        return this._view.clientWidth;
    }

    getClientHeight(): number {
        if (!this._view) {
            return 0;
        }
        return this._view.clientHeight;
    }

    getValues() {
        const {
            scrollLeft = 0,
            scrollTop = 0,
            scrollWidth = 0,
            scrollHeight = 0,
            clientWidth = 0,
            clientHeight = 0,
        } = this._view || {};

        return {
            clientHeight,
            clientWidth,
            left: scrollLeft / (scrollWidth - clientWidth) || 0,
            scrollHeight,
            scrollLeft,
            scrollTop,
            scrollWidth,
            top: scrollTop / (scrollHeight - clientHeight) || 0,
        };
    }

    getThumbHorizontalWidth(): number {
        const { thumbSize, thumbMinSize } = this.props,
            { scrollWidth, clientWidth } = this._view;

        const trackWidth = getInnerWidth(this._trackHorizontal),
            width = Math.ceil((clientWidth / scrollWidth) * trackWidth);

        if (trackWidth === width) {
            return 0;
        }

        if (thumbSize) {
            return thumbSize;
        }

        return Math.max(width, thumbMinSize);
    }

    getThumbVerticalHeight(): number {
        const { thumbSize, thumbMinSize } = this.props,
            { scrollHeight, clientHeight } = this._view;

        const trackHeight = getInnerHeight(this._trackVertical);

        const height = Math.ceil((clientHeight / scrollHeight) * trackHeight);

        if (trackHeight === height) {
            return 0;
        }

        if (thumbSize) {
            return thumbSize;
        }

        return Math.max(height, thumbMinSize);
    }

    getScrollbarWidth(): number {
        if (typeof this._scrollbarWidth !== `undefined`) {
            return this._scrollbarWidth;
        }

        if (typeof document !== `undefined`) {
            const div = document.createElement(`div`);

            css(div, {
                MsOverflowStyle: `scrollbar`,
                height: 100,
                overflow: `scroll`,
                position: `absolute`,
                top: -9999,
                width: 100,
            });

            document.body.appendChild(div);
            this._scrollbarWidth = div.offsetWidth - div.clientWidth;
            document.body.removeChild(div);
        } else {
            this._scrollbarWidth = 0;
        }

        return this._scrollbarWidth || 0;
    }

    getScrollLeftForOffset(offset: number): number {
        const { scrollWidth, clientWidth } = this._view;

        const thumbWidth = this.getThumbHorizontalWidth(),
            trackWidth = getInnerWidth(this._trackHorizontal);

        return (
            (offset / (trackWidth - thumbWidth)) * (scrollWidth - clientWidth)
        );
    }

    getScrollTopForOffset(offset: number): number {
        const { scrollHeight, clientHeight } = this._view;

        const thumbHeight = this.getThumbVerticalHeight(),
            trackHeight = getInnerHeight(this._trackVertical);

        return (
            (offset / (trackHeight - thumbHeight)) *
            (scrollHeight - clientHeight)
        );
    }

    scrollLeft(left = 0): void {
        if (!this._view) {
            return;
        }
        this._view.scrollLeft = left;
    }

    scrollTop(top = 0): void {
        if (!this._view) {
            return;
        }
        this._view.scrollTop = top;
    }

    scrollToLeft(): void {
        if (!this._view) {
            return;
        }
        this._view.scrollLeft = 0;
    }

    scrollToTop(): void {
        if (!this._view) {
            return;
        }
        this._view.scrollTop = 0;
    }

    scrollToRight(): void {
        if (!this._view) {
            return;
        }
        this._view.scrollLeft = this._view.scrollWidth;
    }

    scrollToBottom(): void {
        if (!this._view) {
            return;
        }
        this._view.scrollTop = this._view.scrollHeight;
    }

    addListeners(): void {
        if (typeof document === `undefined` || !this._view) {
            return;
        }

        const {
            _view,
            _trackHorizontal,
            _trackVertical,
            _thumbHorizontal,
            _thumbVertical,
        } = this;

        _view.addEventListener(`scroll`, this.handleScroll);

        if (!this.getScrollbarWidth()) {
            return;
        }

        _trackHorizontal.addEventListener(
            `mouseenter`,
            this.handleTrackMouseEnter,
        );
        _trackHorizontal.addEventListener(
            `mouseleave`,
            this.handleTrackMouseLeave,
        );
        _trackHorizontal.addEventListener(
            `mousedown`,
            this.handleHorizontalTrackMouseDown,
        );
        _trackVertical.addEventListener(
            `mouseenter`,
            this.handleTrackMouseEnter,
        );
        _trackVertical.addEventListener(
            `mouseleave`,
            this.handleTrackMouseLeave,
        );
        _trackVertical.addEventListener(
            `mousedown`,
            this.handleVerticalTrackMouseDown,
        );
        _thumbHorizontal.addEventListener(
            `mousedown`,
            this.handleHorizontalThumbMouseDown,
        );
        _thumbVertical.addEventListener(
            `mousedown`,
            this.handleVerticalThumbMouseDown,
        );
        window.addEventListener(`resize`, this.handleWindowResize);
    }

    removeListeners(): void {
        if (typeof document === `undefined` || !this._view) {
            return;
        }

        const {
            _view,
            _trackHorizontal,
            _trackVertical,
            _thumbHorizontal,
            _thumbVertical,
        } = this;

        _view.removeEventListener(`scroll`, this.handleScroll);

        if (!this.getScrollbarWidth()) {
            return;
        }

        _trackHorizontal.removeEventListener(
            `mouseenter`,
            this.handleTrackMouseEnter,
        );
        _trackHorizontal.removeEventListener(
            `mouseleave`,
            this.handleTrackMouseLeave,
        );
        _trackHorizontal.removeEventListener(
            `mousedown`,
            this.handleHorizontalTrackMouseDown,
        );
        _trackVertical.removeEventListener(
            `mouseenter`,
            this.handleTrackMouseEnter,
        );
        _trackVertical.removeEventListener(
            `mouseleave`,
            this.handleTrackMouseLeave,
        );
        _trackVertical.removeEventListener(
            `mousedown`,
            this.handleVerticalTrackMouseDown,
        );
        _thumbHorizontal.removeEventListener(
            `mousedown`,
            this.handleHorizontalThumbMouseDown,
        );
        _thumbVertical.removeEventListener(
            `mousedown`,
            this.handleVerticalThumbMouseDown,
        );
        window.removeEventListener(`resize`, this.handleWindowResize);
        // Possibly setup by `handleDragStart`
        this.teardownDragging();
    }

    handleScroll(event): void {
        const { onScroll, onScrollFrame } = this.props;

        if (onScroll) {
            onScroll(event);
        }

        this.update((values) => {
            const { scrollLeft, scrollTop } = values;

            this._viewScrollLeft = scrollLeft;
            this._viewScrollTop = scrollTop;
            if (onScrollFrame) {
                onScrollFrame(values);
            }
        });

        this.detectScrolling();
    }

    handleScrollStart(): void {
        const { onScrollStart } = this.props;

        if (onScrollStart) {
            onScrollStart();
        }

        this.handleScrollStartAutoHide();
    }

    handleScrollStartAutoHide(): void {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }

        this.showTracks();
    }

    handleScrollStop(): void {
        const { onScrollStop } = this.props;

        if (onScrollStop) {
            onScrollStop();
        }

        this.handleScrollStopAutoHide();
    }

    handleScrollStopAutoHide(): void {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }

        this.hideTracks();
    }

    handleWindowResize(): void {
        this.update();
    }

    handleHorizontalTrackMouseDown(event): void {
        event.preventDefault();

        const { target, clientX } = event,
            { left: targetLeft } = target.getBoundingClientRect();

        const thumbWidth = this.getThumbHorizontalWidth();

        const offset = Math.abs(targetLeft - clientX) - thumbWidth / 2;

        this._view.scrollLeft = this.getScrollLeftForOffset(offset);
    }

    handleVerticalTrackMouseDown(event): void {
        event.preventDefault();

        const { target, clientY } = event,
            { top: targetTop } = target.getBoundingClientRect();

        const thumbHeight = this.getThumbVerticalHeight();

        const offset = Math.abs(targetTop - clientY) - thumbHeight / 2;

        this._view.scrollTop = this.getScrollTopForOffset(offset);
    }

    handleHorizontalThumbMouseDown(event): void {
        event.preventDefault();

        this.handleDragStart(event);

        const { target, clientX } = event,
            { offsetWidth } = target,
            { left } = target.getBoundingClientRect();

        this._prevPageX = offsetWidth - (clientX - left);
    }

    handleVerticalThumbMouseDown(event): void {
        event.preventDefault();

        this.handleDragStart(event);

        const { target, clientY } = event,
            { offsetHeight } = target,
            { top } = target.getBoundingClientRect();

        this._prevPageY = offsetHeight - (clientY - top);
    }

    setupDragging(): void {
        css(document.body, disableSelectStyle);
        document.addEventListener(`mousemove`, this.handleDrag);
        document.addEventListener(`mouseup`, this.handleDragEnd);
        document.onselectstart = Utils.noop;
    }

    teardownDragging(): void {
        css(document.body, disableSelectStyleReset);
        document.removeEventListener(`mousemove`, this.handleDrag);
        document.removeEventListener(`mouseup`, this.handleDragEnd);
        document.onselectstart = undefined;
    }

    handleDragStart(event): void {
        this._isDragging = true;
        event.stopImmediatePropagation();

        this.setupDragging();
    }

    handleDrag(event): boolean {
        if (this._prevPageX) {
            const { clientX } = event,
                { left: trackLeft } =
                    this._trackHorizontal.getBoundingClientRect();

            const thumbWidth = this.getThumbHorizontalWidth();

            const clickPosition = thumbWidth - this._prevPageX,
                offset = -trackLeft + clientX - clickPosition;

            this._view.scrollLeft = this.getScrollLeftForOffset(offset);
        }

        if (this._prevPageY) {
            const { clientY } = event,
                { top: trackTop } = this._trackVertical.getBoundingClientRect();

            const thumbHeight = this.getThumbVerticalHeight();

            const clickPosition = thumbHeight - this._prevPageY,
                offset = -trackTop + clientY - clickPosition;

            this._view.scrollTop = this.getScrollTopForOffset(offset);
        }

        return false;
    }

    handleDragEnd(): void {
        this._isDragging = false;
        this._prevPageX = this._prevPageY = 0;
        this.teardownDragging();
        this.handleDragEndAutoHide();
    }

    handleDragEndAutoHide(): void {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }
        this.hideTracks();
    }

    handleTrackMouseEnter(): void {
        this._trackMouseOver = true;
        this.handleTrackMouseEnterAutoHide();
    }

    handleTrackMouseEnterAutoHide(): void {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }
        this.showTracks();
    }

    handleTrackMouseLeave(): void {
        this._trackMouseOver = false;
        this.handleTrackMouseLeaveAutoHide();
    }

    handleTrackMouseLeaveAutoHide(): void {
        const { autoHide } = this.props;

        if (!autoHide) {
            return;
        }
        this.hideTracks();
    }

    showTracks(): void {
        clearTimeout(this._hideTracksTimeout);
        css(this._trackHorizontal, { opacity: 1 });
        css(this._trackVertical, { opacity: 1 });
    }

    hideTracks(): void {
        if (this._isDragging || this._isScrolling || this._trackMouseOver) {
            return;
        }
        const { autoHideTimeout } = this.props;

        clearTimeout(this._hideTracksTimeout);
        this._hideTracksTimeout = setTimeout(() => {
            css(this._trackHorizontal, { opacity: 0 });
            css(this._trackVertical, { opacity: 0 });
        }, autoHideTimeout);
    }

    detectScrolling(): void {
        if (this._isScrolling) {
            return;
        }

        this._isScrolling = true;
        this.handleScrollStart();

        this._detectScrollingInterval = setInterval((): void => {
            if (
                this._lastViewScrollLeft === this._viewScrollLeft &&
                this._lastViewScrollTop === this._viewScrollTop
            ) {
                clearInterval(this._detectScrollingInterval);
                this._isScrolling = false;
                this.handleScrollStop();
            }
            this._lastViewScrollLeft = this._viewScrollLeft;
            this._lastViewScrollTop = this._viewScrollTop;
        }, 100);
    }

    requestAnimationFrame(callback?: Function): void {
        if (this._requestFrame) {
            cancelAnimationFrame(this._requestFrame);
        }

        this._requestFrame = requestAnimationFrame((): void => {
            this._requestFrame = undefined;
            callback();
        });
    }

    update(callback?: Function): void {
        this.requestAnimationFrame(() => this._update(callback));
    }

    _update(callback): void {
        const { onUpdate, hideTracksWhenNotNeeded } = this.props,
            values = this.getValues();

        if (this.getScrollbarWidth()) {
            const { scrollLeft, clientWidth, scrollWidth } = values,
                thumbHorizontalWidth = this.getThumbHorizontalWidth(),
                trackHorizontalWidth = getInnerWidth(this._trackHorizontal);

            const thumbHorizontalX =
                    (scrollLeft / (scrollWidth - clientWidth)) *
                    (trackHorizontalWidth - thumbHorizontalWidth),
                thumbHorizontalStyle = {
                    transform: `translateX(${thumbHorizontalX}px)`,
                    width: thumbHorizontalWidth,
                };

            const { scrollTop, clientHeight, scrollHeight } = values,
                thumbVerticalHeight = this.getThumbVerticalHeight(),
                trackVerticalHeight = getInnerHeight(this._trackVertical);

            const thumbVerticalY =
                    (scrollTop / (scrollHeight - clientHeight)) *
                    (trackVerticalHeight - thumbVerticalHeight),
                thumbVerticalStyle = {
                    height: thumbVerticalHeight,
                    transform: `translateY(${thumbVerticalY}px)`,
                };

            if (hideTracksWhenNotNeeded) {
                const trackHorizontalStyle = {
                    visibility:
                        scrollWidth > clientWidth ? `visible` : `hidden`,
                };

                const trackVerticalStyle = {
                    visibility:
                        scrollHeight > clientHeight ? `visible` : `hidden`,
                };

                css(this._trackHorizontal, trackHorizontalStyle);
                css(this._trackVertical, trackVerticalStyle);
            }
            css(this._thumbHorizontal, thumbHorizontalStyle);
            css(this._thumbVertical, thumbVerticalStyle);
        }
        if (onUpdate) {
            onUpdate(values);
        }

        if (typeof callback !== `function`) {
            return;
        }
        callback(values);
    }

    render(): React.ReactElement {
        const scrollbarWidth = this.getScrollbarWidth();

        const {
            autoHeight,
            autoHeightMax,
            autoHeightMin,
            autoHide,
            autoHideDuration,
            autoHideTimeout,
            children,
            hideTracksWhenNotNeeded,
            onScroll,
            onScrollFrame,
            onScrollStart,
            onScrollStop,
            onUpdate,
            renderThumbHorizontal,
            renderThumbVertical,
            renderTrackHorizontal,
            renderTrackVertical,
            renderView,
            style,
            tagName,
            thumbMinSize,
            thumbSize,
            universal,
            ...props
        } = this.props;

        const { didMountUniversal } = this.state;

        const containerStyle = {
            ...containerStyleDefault,
            ...(autoHeight && {
                ...containerStyleAutoHeight,
                maxHeight: autoHeightMax,
                minHeight: autoHeightMin,
            }),
            ...style,
        };

        const viewStyle = {
            ...viewStyleDefault,
            // Hide scrollbars by setting a negative margin
            marginBottom: scrollbarWidth ? -scrollbarWidth : 0,
            marginRight: scrollbarWidth ? -scrollbarWidth : 0,
            ...(autoHeight && {
                ...viewStyleAutoHeight,
                // Add scrollbarWidth to autoHeight in order to compensate negative margins
                maxHeight: Utils.isString(autoHeightMax)
                    ? `calc(${autoHeightMax} + ${scrollbarWidth}px)`
                    : autoHeightMax + scrollbarWidth,
                minHeight: Utils.isString(autoHeightMin)
                    ? `calc(${autoHeightMin} + ${scrollbarWidth}px)`
                    : autoHeightMin + scrollbarWidth,
            }),
            // Override min/max height for initial universal rendering
            ...(autoHeight &&
                universal &&
                !didMountUniversal && {
                    maxHeight: autoHeightMax,
                    minHeight: autoHeightMin,
                }),
            // Override
            ...(universal && !didMountUniversal && viewStyleUniversalInitial),
        };

        const trackAutoHeightStyle = {
            opacity: 0,
            transition: `opacity ${autoHideDuration}ms`,
        };

        const trackHorizontalStyle = {
            ...trackHorizontalStyleDefault,
            ...(autoHide && trackAutoHeightStyle),
            ...((!scrollbarWidth || (universal && !didMountUniversal)) && {
                display: `none`,
            }),
        };

        const trackVerticalStyle = {
            ...trackVerticalStyleDefault,
            ...(autoHide && trackAutoHeightStyle),
            ...((!scrollbarWidth || (universal && !didMountUniversal)) && {
                display: `none`,
            }),
        };

        return (
            // Have to break out of JSX here as we are dynamically creating a tag we can't know about beforehand
            <React.Fragment>
                {React.createElement(
                    tagName,
                    {
                        ...props,
                        ref: (ref) => {
                            this.container = ref;
                        },
                        style: containerStyle,
                    },
                    [
                        React.cloneElement(
                            renderView({
                                style: viewStyle,
                            }),
                            {
                                key: `view`,
                                ref: (ref) => {
                                    this._view = ref;
                                },
                            },
                            children,
                        ),
                        React.cloneElement(
                            renderTrackHorizontal({
                                style: trackHorizontalStyle,
                            }),
                            {
                                key: `trackHorizontal`,
                                ref: (ref) => {
                                    this._trackHorizontal = ref;
                                },
                            },
                            React.cloneElement(
                                renderThumbHorizontal({
                                    style: thumbHorizontalStyleDefault,
                                }),
                                {
                                    ref: (ref) => {
                                        this._thumbHorizontal = ref;
                                    },
                                },
                            ),
                        ),
                        React.cloneElement(
                            renderTrackVertical({
                                style: trackVerticalStyle,
                            }),
                            {
                                key: `trackVertical`,
                                ref: (ref) => {
                                    this._trackVertical = ref;
                                },
                            },
                            React.cloneElement(
                                renderThumbVertical({
                                    style: thumbVerticalStyleDefault,
                                }),
                                {
                                    ref: (ref) => {
                                        this._thumbVertical = ref;
                                    },
                                },
                            ),
                        ),
                    ],
                )}
            </React.Fragment>
        );
    }
}

/* STYLING FOR DEFAULTS */
const containerStyleDefault = {
    height: `100%`,
    overflow: `hidden`,
    position: `relative`,
    width: `100%`,
};

// Overrides containerStyleDefault properties
const containerStyleAutoHeight = {
    height: `auto`,
};

const viewStyleDefault = {
    WebkitOverflowScrolling: `touch`,
    bottom: 0,
    display: `flex`,
    flexDirection: `column`,
    left: 0,
    overflow: `scroll`,
    position: `absolute`,
    right: 0,
    top: 0,
};

// Overrides viewStyleDefault properties
const viewStyleAutoHeight = {
    bottom: undefined,
    left: undefined,
    position: `relative`,
    right: undefined,
    top: undefined,
};

const viewStyleUniversalInitial = {
    marginBottom: 0,
    marginRight: 0,
    overflow: `hidden`,
};

const trackHorizontalStyleDefault = {
    height: 6,
    position: `absolute`,
};

const trackVerticalStyleDefault = {
    position: `absolute`,
    width: 6,
};

const thumbHorizontalStyleDefault = {
    display: `block`,
    height: `100%`,
    position: `relative`,
};

const thumbVerticalStyleDefault = {
    display: `block`,
    position: `relative`,
    width: `100%`,
};

const disableSelectStyle = {
    userSelect: `none`,
};

const disableSelectStyleReset = {
    userSelect: ``,
};

function renderViewDefault(props): JSX.Element {
    return <div {...props} />;
}

function renderTrackHorizontalDefault({ style, ...props }): JSX.Element {
    const finalStyle = {
        ...style,
        borderRadius: 3,
        bottom: 2,
        left: 2,
        right: 2,
    };

    return <div style={finalStyle} {...props} />;
}

function renderTrackVerticalDefault({ style, ...props }): JSX.Element {
    const finalStyle = {
        ...style,
        borderRadius: 3,
        bottom: 2,
        right: 2,
        top: 2,
    };

    return <div style={finalStyle} {...props} />;
}

function renderThumbHorizontalDefault({ style, ...props }): JSX.Element {
    const finalStyle = {
        ...style,
        backgroundColor: `rgba(0,0,0,.4)`,
        borderRadius: `inherit`,
        cursor: `pointer`,
    };

    return <div style={finalStyle} {...props} />;
}

function renderThumbVerticalDefault({ style, ...props }): JSX.Element {
    const finalStyle = {
        ...style,
        backgroundColor: `rgba(0,0,0,.4)`,
        borderRadius: `inherit`,
        cursor: `pointer`,
    };

    return <div style={finalStyle} {...props} />;
}

function getInnerWidth(el: HTMLElement): number {
    const { clientWidth } = el,
        { paddingLeft, paddingRight } = getComputedStyle(el);

    return clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);
}

function getInnerHeight(el: HTMLElement): number {
    const { clientHeight } = el,
        { paddingTop, paddingBottom } = getComputedStyle(el);

    return clientHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);
}
